import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { ScreenErrorBoundary } from './ScreenErrorBoundary'
import { NavigationGuard, useLayer } from './layers'
import { useTextSize } from '../lib/textSize'
import { AccountPopup, TextSizeButtons } from './Appearance'
import { BrandMark, ReleaseNote } from './Shell'
import styles from './Shell.module.css'

// God Mode's frame: the workspace Shell's layout and classes exactly, in God
// Mode's colours (.godMode in Shell.module.css), with its own rail. Nothing
// here belongs to a client workspace: no search, no task centre, no
// workspace rail.

const ROWS = [
  { label: 'Dashboard', to: '/god-mode', paths: ['/god-mode'], match: (p: string) => p === '/god-mode' },
  { label: 'Clients', to: '/god-mode/clients', paths: ['/god-mode/clients'], match: (p: string) => p.startsWith('/god-mode/clients') && !p.endsWith('/varieties') },
  { label: 'New client', to: '/god-mode/new', paths: ['/god-mode/new'], match: (p: string) => p === '/god-mode/new' },
  { label: 'Legacy clients', to: '/god-mode/legacy', paths: ['/god-mode/legacy'], match: (p: string) => p === '/god-mode/legacy' },
  { label: 'Shared tags', to: '/god-mode/tags', paths: ['/god-mode/tags'], match: (p: string) => p === '/god-mode/tags' },
  { label: 'Tags', to: '/god-mode/all-tags', paths: ['/god-mode/all-tags'], match: (p: string) => p === '/god-mode/all-tags' },
  { label: 'Varieties', to: '/god-mode/varieties', paths: ['/god-mode/varieties'], match: (p: string) => p === '/god-mode/varieties' || (p.startsWith('/god-mode/clients/') && p.endsWith('/varieties')) },
  { label: 'Tag stock', to: '/god-mode/stock', paths: ['/god-mode/stock'], match: (p: string) => p === '/god-mode/stock' },
  { label: 'Label formats', to: '/god-mode/label-formats', paths: ['/god-mode/label-formats'], match: (p: string) => p === '/god-mode/label-formats' },
  { label: 'Queues', to: '/god-mode/queues', paths: ['/god-mode/queues'], match: (p: string) => p === '/god-mode/queues' },
  { label: 'API', to: '/god-mode/api', paths: ['/god-mode/api'], match: (p: string) => p === '/god-mode/api' },
]

export function GodModeShell({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth()
  const { subscriptions, openPicker } = useWorkspace()
  const location = useLocation()
  const navigate = useNavigate()
  const [railOpen, setRailOpen] = useState(false)
  useLayer(railOpen, () => setRailOpen(false))
  const [textSize, setTextSize] = useTextSize(user)
  const [accountOpen, setAccountOpen] = useState(false)
  const accountBtn = useRef<HTMLButtonElement>(null)
  const closeAccount = useCallback(() => setAccountOpen(false), [])

  useEffect(() => {
    setRailOpen(false)
    setAccountOpen(false)
  }, [location.pathname])

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign-out failed:', err)
    }
  }

  const here = location.pathname.replace(/\/+$/, '') || '/'
  const name = user?.displayName || user?.email || 'User'

  return (
    <div className={`${styles.container} ${styles.godMode}`} data-screen-label="God Mode">
      {railOpen && <div className={styles.scrim} onClick={() => setRailOpen(false)} aria-hidden="true" />}
      <nav className={`${styles.sidebar} ${railOpen ? styles.sidebarOpen : ''}`} aria-label="God Mode">
        {/* The brand block leaves God Mode the way the Shell's switches
            workspace: to the picker, or straight into the only workspace. */}
        {subscriptions.length > 0 ? (
          <button
            type="button"
            className={`${styles.brandBlock} ${styles.brandSwitch}`}
            onClick={() => {
              openPicker()
              navigate('/dashboard')
            }}
            title={subscriptions.length > 1 ? 'Leave God Mode: choose a workspace' : `Leave God Mode: back to ${subscriptions[0].name}`}
          >
            <BrandMark />
            <span className={styles.brandText}>
              <span className={styles.productName}>GOD MODE</span>
              <span className={styles.workspaceLine}>
                <span className={styles.workspaceName}>Dataphyll staff</span>
                <svg className={styles.brandArrow} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
            </span>
          </button>
        ) : (
          <div className={styles.brandBlock} title="God Mode: Dataphyll staff only">
            <BrandMark />
            <span className={styles.brandText}>
              <span className={styles.productName}>GOD MODE</span>
              <span className={styles.workspaceLine}>
                <span className={styles.workspaceName}>Dataphyll staff</span>
              </span>
            </span>
          </div>
        )}

        <div className={styles.navSections}>
          <div className={styles.navSection}>
            <h3 className={styles.sectionLabel}>Clients</h3>
            <ul className={styles.navList}>
              {ROWS.map((row) => {
                const on = row.match(here)
                return (
                  <li key={row.label}>
                    <Link
                      to={row.to}
                      className={`${styles.navItem} ${on ? styles.active : ''}`}
                      aria-current={on ? 'page' : undefined}
                      title={on ? 'You are here' : `Go to ${row.label}`}
                    >
                      <span className={styles.dot} aria-hidden="true" />
                      {row.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <TextSizeButtons size={textSize} onChange={setTextSize} />

        <button
          ref={accountBtn}
          type="button"
          className={styles.userChip}
          onClick={() => setAccountOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={accountOpen}
          title="Appearance and account"
        >
          <span className={styles.userInitials} aria-hidden="true">
            {user?.displayName
              ?.split(' ')
              .map((n: string) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase() || 'U'}
          </span>
          <span className={styles.userInfo}>
            <span className={styles.userName}>{name}</span>
            <span className={styles.userRole}>Dataphyll staff</span>
          </span>
        </button>
        <ReleaseNote />
        {accountOpen && (
          <AccountPopup
            anchor={accountBtn}
            name={name}
            email={user?.email || ''}
            workspace="God Mode"
            size={textSize}
            onSize={setTextSize}
            onSignOut={handleSignOut}
            onClose={closeAccount}
          />
        )}
      </nav>

      <div className={styles.mainArea}>
        <header className={styles.phoneBar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setRailOpen(true)}
            aria-label="Open menu"
            aria-expanded={railOpen}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className={styles.phoneTitle}>God Mode</span>
        </header>

        <main className={styles.pageContent}>
          <ScreenErrorBoundary key={here}>{children}</ScreenErrorBoundary>
        </main>
        <NavigationGuard />
      </div>
    </div>
  )
}
